import { useGetMulticityResultsQuery, useGetSrpResultsQuery } from '@/src/api/rtkQuery/srpFlightResults'
import { MulticitySearchBody } from '@/src/types/searchQuery'
import {
    selectFlightsAfterFilterCount,
    selectOnwardFilteredCards,
    selectOnwardRecommendedFilter,
    selectOnwardSort,
    selectReturnFilteredCards,
    selectReturnRecommendedFilter,
    selectReturnSort,
    selectShowEmptyFlightScreen
} from '@/src/redux/selectors/srpSelectors'
import { getRecommendedFilter, getSelectedSort } from '@/src/redux/slices/srpStatesSlice'
import { useAppSelector } from '@/src/redux/store'
import { SHOW_SMART_METRICS } from '@/src/utils/srp/aiRecommendation/metrics'
import type { RtLegIndex } from '@/src/types/srpJourneyIndex'
import { useMemo } from 'react'
import useGetSearchQueryURL from '../getSearchQueryURL'

/**
 * This hook is used to get the search results from the API with RTK Query.
 * multiple instances of this hook always return updated sync data as it's all states from redux.
 * - call this hooks to get the applied filters and sorted cards.
 *- NOTE: Don't create local states in this hook.
 *
 * @param rtLegOverride - Only for OW/RT non-solution-view. Multicity: leave default 0.
 *   activeRtLeg is always 0 for multicity (single card list in cards[0]).
 */
const useSearchResults = (rtLegOverride: RtLegIndex = 0) => {
    const {
        query,
        error: invalidUrl,
        isRT,
        isSolutionView,
        isPerPaxPricingRequired,
        isMulticity,
        multicityBody,
        journeyCount
    } = useGetSearchQueryURL()

    const srpResults = useGetSrpResultsQuery(
        { query },
        {
            skip: !query || !!invalidUrl || !!isMulticity
        }
    )

    const multicityResults = useGetMulticityResultsQuery(multicityBody as MulticitySearchBody, {
        skip: !isMulticity || !multicityBody || !!invalidUrl
    })

    const {
        data,
        isLoading,
        isFetching,
        error: srpResultsError,
        refetch,
        isUninitialized
    } = isMulticity ? multicityResults : srpResults

    const shouldShowMultiAirlinePrices = !!(isRT && isSolutionView)
    const srpResultsLoading = isLoading || isFetching

    const selectedSort = useAppSelector(getSelectedSort)
    const recommendedFilter = useAppSelector(getRecommendedFilter)

    const { cards, sortData, filterData, jsons, sectors, sid, solutionFlowSelectedLeg = 0, smartRecommendationData } = data || {}
    const isPerPaxPricingEnabled = !!isPerPaxPricingRequired && !!jsons?.isPerPaxPricingEnabled

    const activeRtLeg: RtLegIndex = isMulticity
        ? 0
        : isSolutionView
          ? solutionFlowSelectedLeg
          : rtLegOverride

    // Use pre-created selectors based on active RT leg - these are shared across all hook instances
    const selectedSortForSelectedJourneyLeg = useAppSelector(
        activeRtLeg === 0 ? selectOnwardSort : selectReturnSort
    )

    const selectedJourneyLegRecommendedFilter = useAppSelector(
        activeRtLeg === 0 ? selectOnwardRecommendedFilter : selectReturnRecommendedFilter
    )


    const selectedLegSortedFilteredData = useAppSelector(state =>
        activeRtLeg === 0
            ? selectOnwardFilteredCards(
                state,
                data,
                shouldShowMultiAirlinePrices,
                srpResultsLoading,
                isPerPaxPricingEnabled
            )
            : selectReturnFilteredCards(
                state,
                data,
                shouldShowMultiAirlinePrices,
                srpResultsLoading,
                isPerPaxPricingEnabled
            )
    )

    const selectedLegSortedFilteredCards = selectedLegSortedFilteredData?.filteredCards || []
    const smartSortIdMetrics = selectedLegSortedFilteredData.smartSortIdMetrics
    const selectedLegGroupedCards = selectedLegSortedFilteredData?.groupedCards || []
    const smartPreferenceGroupedCards = selectedLegSortedFilteredData?.smartPreferenceGroupedCards || null

    const selectedJourneyLegCheckedBaggageIncludedRecommendedFilter =
        selectedJourneyLegRecommendedFilter?.checkedBaggageIncluded || false

    // Memoized calculations for flight counts
    const totalFlights = useMemo(
        () => (activeRtLeg === 0 ? cards?.[0]?.length : cards?.[1]?.length),
        [activeRtLeg, cards]
    )

    const numberOfFlightsAfterFilter = useMemo(
        () => selectFlightsAfterFilterCount(selectedLegSortedFilteredCards),
        [selectedLegSortedFilteredCards]
    )

    const showEmptyFlightScreen = useMemo(
        () => selectShowEmptyFlightScreen(selectedLegSortedFilteredCards),
        [selectedLegSortedFilteredCards]
    )




    return {
        srpResultsLoading,
        srpResultsError: srpResultsError as {
            data: Record<string, any>
            status: number
        },
        invalidUrl,
        isSolutionView,
        isRT,
        isMulticity: !!isMulticity,
        journeyCount: journeyCount ?? 1,
        filterData,
        sortData,
        selectedSortForSelectedJourneyLeg,
        selectedLegSortedFilteredCards,
        selectedLegGroupedCards,
        smartPreferenceGroupedCards,
        jsons,
        sectors,
        /** Active RT leg (0=onward, 1=return). Multicity: always 0. */
        activeRtLeg,
        /** @deprecated Use activeRtLeg — same value, kept for existing OW/RT call sites */
        selectedJourneyLeg: activeRtLeg,
        sid,
        totalFlights,
        numberOfFlightsAfterFilter,
        showEmptyFlightScreen,
        cards,
        selectedSort,
        shouldShowMultiAirlinePrices,
        refetch,
        isUninitialized,
        query,
        isPerPaxPricingEnabled,
        smartSortIdMetrics: SHOW_SMART_METRICS ? smartSortIdMetrics : undefined,
        smartRecommendationData: smartRecommendationData?.[activeRtLeg] ?? null
    }
}

export default useSearchResults
